import axios from 'axios';
import { Video, CreateVideoDto, UpdateVideoDto, VideoStats } from '../types/video';
import { getApiBaseUrl } from '../config/api';

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token and user data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const videoApi = {
  // Get all videos
  getAll: async (params?: { category?: string; tag?: string; published?: boolean }): Promise<Video[]> => {
    const response = await api.get('/videos', { params });
    return response.data;
  },

  // Get videos with pagination
  getPaginated: async (params: { 
    page?: number; 
    pageSize?: number; 
    category?: string; 
    tag?: string; 
    published?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    search?: string;
  }): Promise<{
    data: Video[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> => {
    const response = await api.get('/videos/paginated', { params });
    return response.data;
  },

  // Get video by ID
  getById: async (id: string): Promise<Video> => {
    const response = await api.get(`/videos/${id}`);
    return response.data;
  },

  // Create new video
  create: async (data: CreateVideoDto): Promise<Video> => {
    const response = await api.post('/videos', data);
    return response.data;
  },

  // Update video
  update: async (id: string, data: UpdateVideoDto): Promise<Video> => {
    const response = await api.patch(`/videos/${id}`, data);
    return response.data;
  },

  // Delete video
  delete: async (id: string): Promise<void> => {
    await api.delete(`/videos/${id}`);
  },

  // Bulk delete videos
  bulkDelete: async (ids: string[]): Promise<{ deletedCount: number; notFoundIds: string[] }> => {
    const response = await api.post('/videos/bulk-delete', { ids });
    return response.data;
  },

  // Get video statistics
  getStats: async (): Promise<VideoStats> => {
    const response = await api.get('/videos/stats');
    return response.data;
  },

  // Increment view count
  incrementView: async (id: string): Promise<Video> => {
    const response = await api.post(`/videos/${id}/view`);
    return response.data;
  },

  // Import videos from CSV
  importVideos: async (file: File): Promise<{ success: number; errors: string[] }> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/videos/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

export default api;
